import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  CITY_REGION_MAP,
  type City,
  type Language,
  type Specialization,
  type SubscriptionStatus,
  type UserRegion,
} from '@pomoshnik/shared';

/**
 * Auth store — Block 0 Phase 2 (ADR-0012 §2) + Phase 4 (ADR-0014 §«Решение» Q3).
 *
 * Первый Zustand store в проекте. Хранит идентичность пользователя, согласия
 * на обработку ПДн и subscription state. Persist middleware сохраняет
 * белый-список полей в `localStorage` под ключом `pomoshnik-auth-store-v1`.
 *
 * ── Persist policy (P-009 минимизация ПДн) ────────────────────────────────
 * В localStorage сохраняются ТОЛЬКО:
 *   - `lang`, `city`, `region` — UX continuity (после logout пользователь не
 *     перевыбирает город).
 *   - `jwt` — mock в Phase 2; будет переключён на httpOnly cookie в Phase 9+
 *     (см. ADR-0012 §«Недостатки» #2).
 *   - `consents.marketingAnalytics` — отдельный флаг (P-035), survive sessions.
 *
 * НИКОГДА не persisted (только in-memory):
 *   - `profile.name`, `profile.phone`, `profile.specialization` — ПДн.
 *     После refresh/rehydration форма заново запрашивает их у сервера
 *     (Phase 9+) либо заставляет повторно зарегистрироваться (Phase 2).
 *   - `consents.dataProcessing` — moment-in-time consent для регистрации.
 *     На сервере (Phase 9+) запись хранится в `user_consents`; клиенту нужен
 *     только в момент submit формы.
 *   - `subscription` — server source of truth (ADR-0014 Q3). После refresh
 *     App.tsx mount fetch'ит `/api/me` и восстанавливает subscription через
 *     `setSubscription`. Persist'ить запрещено: (а) attack surface (DevTools
 *     manipulation = пользователь «продлевает» trial); (б) stale data между
 *     rehydrate и первым fetch — UI вводит в заблуждение.
 *
 * Persist whitelist реализован через `partialize`. Опциональный `merge`
 * восстанавливает полную форму store-state из subset, явно сбрасывая
 * `profile=null` и `consents.dataProcessing=false` при rehydrate.
 *
 * ── Region immutable (P-008) ──────────────────────────────────────────────
 * До `setProfile` — `setLangCity` свободно меняет lang/city/region (юзер
 * передумал, выбрал другой город). После `setProfile` (registered) —
 * `setLangCity` бросает `RegionImmutableError`, если новый город принадлежит
 * другому региону. В пределах одного региона смена города допустима
 * (UX-кейс: переезд из Алматы в Караганду внутри KZ).
 *
 * ── localStorage versioning ───────────────────────────────────────────────
 * Ключ `pomoshnik-auth-store-v1`. При структурных изменениях store →
 * `-v2`; старые `-v1` orphan безвредны (см. ADR-0012 §«Откат»).
 */

export type AuthProfile = {
  name: string;
  phone: string;
  specialization: Specialization;
};

export type AuthConsents = {
  dataProcessing: boolean;
  marketingAnalytics: boolean;
};

/**
 * Subscription state — Block 0 Phase 4 (ADR-0014 §«Решение» Q3).
 *
 * Hard-mirror `MeResponseSchema` (subset без identity-полей `userId`/`region`).
 * 5 канонических состояний из data-model.md §11. Live in-memory only —
 * `partialize` НЕ включает это поле.
 */
export type AuthSubscription = {
  status: SubscriptionStatus;
  /** ISO 8601 — когда начался trial (фиксируется один раз при регистрации). */
  trialStartedAt: string;
  /** ISO 8601 — когда заканчивается ТЕКУЩИЙ status. */
  expiresAt: string;
};

export type AuthState = {
  // ── persisted в localStorage ──
  lang: Language | null;
  city: City | null;
  region: UserRegion | null;
  jwt: string | null;
  consents: AuthConsents;
  // ── только in-memory (НЕ persisted, P-009) ──
  profile: AuthProfile | null;
  /**
   * Subscription state from `GET /api/me`. Phase 4 (ADR-0014 Q3).
   * `null` означает «не загружено» — UI должен показывать loading или
   * fallback (PaywallModal в Home.tsx — defensive default).
   */
  subscription: AuthSubscription | null;
};

export type SetProfileInput = {
  profile: AuthProfile;
  jwt: string;
  consents: AuthConsents;
};

/**
 * Поля, редактируемые на экране 2.11.2 «Редактирование профиля» (Block 11
 * Phase 11.B, ADR-0091). Телефон НЕ здесь — он меняется отдельным OTP-флоу
 * (P-010); регион НЕ здесь — он immutable (P-008), `city` обязан остаться в
 * текущем регионе (иначе `RegionImmutableError`).
 */
export type ProfileFieldsInput = {
  name: string;
  specialization: Specialization;
  city: City;
};

export type AuthActions = {
  /**
   * Устанавливает язык и город, выводит регион из CITY_REGION_MAP.
   * После регистрации (`profile != null`) меняет lang/city только в пределах
   * текущего региона; на mismatch бросает `RegionImmutableError` (P-008).
   */
  setLangCity: (lang: Language, city: City) => void;
  /**
   * Завершает регистрацию (Stage 2). Записывает profile + jwt + consents.
   * `profile` остаётся в memory; `jwt` и `consents.marketingAnalytics`
   * persisted (см. partialize).
   */
  setProfile: (input: SetProfileInput) => void;
  /**
   * Применяет правку профиля с экрана 2.11.2 (Block 11 Phase 11.B, ADR-0091).
   * Обновляет `name`/`specialization` в `profile` (телефон сохраняется как был)
   * и `city`. **Регион immutable (P-008):** если `CITY_REGION_MAP[city]`
   * отличается от текущего `region` — бросает `RegionImmutableError` (фронт
   * фильтрует дропдаун городов по региону, так что в нормальном flow не
   * случается). Требует установленного `region` (онбординг пройден), иначе
   * `OnboardingOrderError`. Если `profile === null` (in-memory сброшен после
   * refresh, P-009) — телефон неизвестен, сохраняется пустым; в нормальном
   * flow (переход из хаба в той же сессии) телефон присутствует и сохраняется.
   */
  setProfileFields: (input: ProfileFieldsInput) => void;
  /**
   * Сохраняет subscription state (Phase 4, ADR-0014). Вызывается App.tsx
   * после успешного `apiClient.me.get()`. In-memory only.
   */
  setSubscription: (subscription: AuthSubscription) => void;
  /**
   * Очищает subscription state (Phase 4, ADR-0014). Вызывается:
   * - logout() как часть полной очистки.
   * - Manually для testing / reset flows.
   */
  clearSubscription: () => void;
  /**
   * Logout — clear `jwt`, `profile`, `consents`, `subscription`. KEEP `lang`,
   * `city`, `region` (UX continuity per PRD «гостевой вход», ADR-0012 §8).
   */
  logout: () => void;
  /** `true` если язык + город + регион установлены. */
  isOnboarded: () => boolean;
  /** `true` если профиль и JWT присутствуют. */
  isRegistered: () => boolean;
  /**
   * Сколько целых дней осталось до `subscription.expiresAt` (Phase 4, Q4).
   * `null` если subscription не загружен. `0` если expiresAt в прошлом
   * (грейс/expired/archived без активного counter'а — UI не показывает «-N»).
   *
   * Recalculate at render — нет polling, stale-but-acceptable. Производит
   * `Math.ceil((expiresAt - Date.now()) / 86_400_000)`. Edge cases:
   *   - expiresAt уже прошёл → 0 (а не отрицательное число).
   *   - expiresAt через ~1 час → 1 (ceil закругляет вверх — пользователь
   *     не видит «осталось 0 дней» когда осталось ~57 минут).
   */
  getDaysRemaining: () => number | null;
};

export type AuthStore = AuthState & AuthActions;

export const AUTH_STORE_KEY = 'pomoshnik-auth-store-v1';

const INITIAL_STATE: AuthState = {
  lang: null,
  city: null,
  region: null,
  jwt: null,
  consents: { dataProcessing: false, marketingAnalytics: false },
  profile: null,
  subscription: null,
};

const DAY_MS = 86_400_000;

/**
 * Бросается при попытке вызвать `setProfile` без предварительного `setLangCity`
 * (B-060, P-008 invariant). Защищает store от inconsistent state
 * `profile != null && region === null`, который обходит P-008 immutability
 * guard через `state.region === null` short-circuit в `setLangCity`.
 *
 * Реальный flow всегда упорядочен через router (Stage 1 → Stage 2), так что
 * exception — strictly programming error / test misuse, а не user-facing.
 */
export class OnboardingOrderError extends Error {
  constructor() {
    super(
      'setLangCity must precede setProfile (P-008 invariant). ' +
        'Call setLangCity(lang, city) before setProfile() to derive region.',
    );
    this.name = 'OnboardingOrderError';
  }
}

/**
 * Бросается при попытке сменить регион после регистрации (P-008).
 * Поля для UI/тестов — current/attempted region + город-источник.
 */
export class RegionImmutableError extends Error {
  public readonly currentRegion: UserRegion;
  public readonly attemptedRegion: UserRegion;
  public readonly attemptedCity: City;

  constructor(currentRegion: UserRegion, attemptedRegion: UserRegion, attemptedCity: City) {
    super(
      `Регион пользователя зафиксирован (${currentRegion}) после регистрации. ` +
        `Город «${attemptedCity}» принадлежит региону ${attemptedRegion}. ` +
        `Смена региона возможна только через админ-панель (P-008).`,
    );
    this.name = 'RegionImmutableError';
    this.currentRegion = currentRegion;
    this.attemptedRegion = attemptedRegion;
    this.attemptedCity = attemptedCity;
  }
}

type PersistedShape = {
  lang: Language | null;
  city: City | null;
  region: UserRegion | null;
  jwt: string | null;
  consents: { marketingAnalytics: boolean };
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setLangCity: (lang, city) => {
        const newRegion = CITY_REGION_MAP[city];
        const state = get();
        if (state.profile !== null && state.region !== null && state.region !== newRegion) {
          throw new RegionImmutableError(state.region, newRegion, city);
        }
        set({ lang, city, region: newRegion });
      },

      setProfile: ({ profile, jwt, consents }) => {
        // B-060 — precondition assert: lang/city/region обязаны быть установлены
        // до регистрации. Защищает от inconsistent state, при котором
        // `setLangCity` после `setProfile` обходит P-008 immutability guard
        // через short-circuit `state.region === null`.
        if (get().region === null) {
          throw new OnboardingOrderError();
        }
        set({ profile, jwt, consents });
      },

      setProfileFields: ({ name, specialization, city }) => {
        const state = get();
        // Онбординг обязан быть пройден (region выведен) — иначе нельзя
        // редактировать профиль (симметрия с setProfile precondition).
        if (state.region === null) {
          throw new OnboardingOrderError();
        }
        const newRegion = CITY_REGION_MAP[city];
        // P-008: город нельзя сменить так, чтобы сменился регион.
        if (state.region !== newRegion) {
          throw new RegionImmutableError(state.region, newRegion, city);
        }
        set({
          city,
          profile: {
            name,
            specialization,
            // Телефон вне правки 2.11.2 (отдельный OTP-флоу, P-010) — сохраняем
            // как был; '' только в degraded-кейсе сброшенного in-memory profile.
            phone: state.profile?.phone ?? '',
          },
        });
      },

      setSubscription: (subscription) => {
        set({ subscription });
      },

      clearSubscription: () => {
        set({ subscription: null });
      },

      logout: () => {
        // ADR-0012 §8 + ADR-0014 Q3: clear jwt+profile+consents+subscription,
        // keep lang/city/region.
        set({
          jwt: null,
          profile: null,
          consents: { dataProcessing: false, marketingAnalytics: false },
          subscription: null,
        });
      },

      isOnboarded: () => {
        const s = get();
        return s.lang !== null && s.city !== null && s.region !== null;
      },

      isRegistered: () => {
        const s = get();
        return s.profile !== null && s.jwt !== null;
      },

      getDaysRemaining: () => {
        const sub = get().subscription;
        if (sub === null) return null;
        const expiresMs = Date.parse(sub.expiresAt);
        // Невалидный ISO 8601 → NaN. Считаем как «истёк» и возвращаем 0,
        // а не null — null означает «не загружено», 0 означает «прошло».
        if (Number.isNaN(expiresMs)) return 0;
        const diffMs = expiresMs - Date.now();
        if (diffMs <= 0) return 0;
        return Math.ceil(diffMs / DAY_MS);
      },
    }),
    {
      name: AUTH_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // P-009 whitelist — только эти поля попадают в localStorage.
      partialize: (state): PersistedShape => ({
        lang: state.lang,
        city: state.city,
        region: state.region,
        jwt: state.jwt,
        consents: { marketingAnalytics: state.consents.marketingAnalytics },
      }),
      // Восстанавливает полный shape после rehydrate, явно обнуляя
      // непереданные поля (profile, consents.dataProcessing, subscription).
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<PersistedShape>;
        return {
          ...currentState,
          lang: persisted.lang ?? currentState.lang,
          city: persisted.city ?? currentState.city,
          region: persisted.region ?? currentState.region,
          jwt: persisted.jwt ?? currentState.jwt,
          consents: {
            dataProcessing: false,
            marketingAnalytics: persisted.consents?.marketingAnalytics ?? false,
          },
          profile: null,
          // Phase 4 (ADR-0014 Q3): subscription — server source of truth,
          // никогда не persist; после rehydrate App.tsx fetch'ит /api/me.
          subscription: null,
        };
      },
    },
  ),
);

/**
 * Тестовый утилит: сбрасывает store в initial state без затрагивания
 * actions. Использовать в `beforeEach` тестов, которые работают с store.
 */
export function resetAuthStoreForTests(): void {
  useAuthStore.setState(INITIAL_STATE);
}
